import Button from "@/common/elements/Button";
import Table from "@/common/elements/Table";
import { StyledFlex, StyledGridCol, StyledGridRow } from "@/common/styles";
import { PlusOutlined } from "@ant-design/icons";
import { useEffect, useRef, useState } from "react";
import BookingHoldsModal from "./BookingHoldsModal";
import { useDispatch, useSelector } from "react-redux";
import { setBookings, setDeleteAPis } from "@/slices/bookingsSlice";
import { v4 as uuidv4 } from "uuid";
import { Popconfirm } from "antd";
import {
  getOptionsFromRefList,
  hasCheckSecurity,
  isNonEmptyVariable,
} from "@/util/common_function";
import dayjs from "dayjs";
import API_ENDPOINTS from "@/util/api";
import { Entity_types, Security_Types } from "@/util/permission_bits";
import { LABELS_CAPITAL } from "@/util/usePermissionMask";
import {
  MODULES_LIST_WITH_ID,
  SUB_MODULES_LIST_WITH_ID,
} from "@/util/common_variable";
import useGridColumnConfig from "@/hooks/useGridColumnConfig";

const HOLDS_HEADERS = [
  { key: "id", text: "#" },
  { key: "holdReasonLbl", text: "Hold Reason" },
  { key: "holdFor", text: "Hold For" },
  { key: "holdStatusLbl", text: "Hold Status" },
  { key: "dateAdded", text: "Date Added" },
  { key: "timeAdded", text: "Time Added" },
];

const BookingHolds = ({startingPoint = ""}) => {
  const dispatch = useDispatch();
  const { bookings, bookingsFieldsEnabled } = useSelector(
    (state) => state.bookings
  );

  // "Config" — drag-reorder/resize columns, persisted per-user (see
  // docs/plans/grid-column-config-feature.md).
  const {
    headers: holdsHeaders,
    isConfiguring,
    configButtonLabel,
    startConfiguring,
    confirmSave,
    discardChanges,
    dataTableRef,
  } = useGridColumnConfig("Bookings:Holds", HOLDS_HEADERS);

  const holdFormRef = useRef();
  const [holdsModal, setHoldsModal] = useState(false);
  const [holdsData, setHoldsData] = useState([]);
  const [holdsTableData, setHoldsTableData] = useState([]);
  const [selectedHold, setSelectedHold] = useState();
  const [isEdit, setisEdit] = useState(false);

  useEffect(() => {
    cancelAction();
  }, [bookingsFieldsEnabled]);

  useEffect(() => {
    if (bookings !== null) {
      let holds = bookings?.holds;
      holds = holds?.map((h, i) => {
        return {
          ...h,
          id: i + 1,
          uuid: uuidv4(),
        };
      });
      setHoldsData(holds || []);
      setHoldsTableData((prev) =>
        holds?.map((item) => {
          return {
            ...item,
            holdReasonLbl: getOptionsFromRefList(
              "Hold_Reason",
              false,
              false,
              true
            ).filter((data) => parseInt(item.holdReason) === data?.value)?.[0]
              ?.label,
            holdStatusLbl: getOptionsFromRefList(
              "Hold_Status",
              false,
              false,
              true
            ).filter((data) => parseInt(item.holdStatus) === data?.value)?.[0]
              ?.label,
          };
        })
      );
    } else {
      setHoldsData([]);
      setHoldsTableData([]);
    }
  }, [bookings]);

  const handleHoldsModal = () => {
    setHoldsModal(!holdsModal);
  };

  const patchSelectedHoldData = (data) => {
    setSelectedHold(data);
  };

  const onAddHolds = () => {
    cancelAction();
    handleHoldsModal();
  };

  const editHold = () => {
    setisEdit(true);
    setHoldsModal(true);
  };

  const formatDateTime = (dateTime) =>
    isNonEmptyVariable(dateTime)
      ? dayjs(dateTime).format("MM/DD/YYYY HH:mm")
      : null;

  const updateStoreObj = () => {
    holdFormRef?.current?.form?.validateFields().then((value) => {
      const dateFields = [
        "clearanceAuthorizationDateTime",
        "transferAuthorizationDateTime",
        "transferDateTime",
        "transferReleaseDateTime",
        "expiresDateTime",
      ];

      dateFields.forEach((field) => {
        const formatted = formatDateTime(value?.[field]);
        if (formatted) {
          const [date, time] = formatted.split(" ");
          if (field === "transferDateTime") {
            value[field.replace("DateTime", "Date")] = date;
            value[field.replace("DateTime", "Time")] = time;
            value[field] = formatted;
          } else {
            value[field.replace("DateTime", "Date")] = date;
            value[field.replace("DateTime", "Time")] = time;
            delete value[field]; // Remove original datetime field after extracting parts
          }
        }
      });

      // Split HoldsExtra dynamic fields out of the form values — nested onto
      // holdsExtra[0] here (never sent embedded in the main bookings save
      // payload, see pages/Bookings/index.jsx's strip-before-POST), same
      // spot BookingBailsExtra stashes theirs on bookingBails entries. A
      // brand new hold has no holdID yet, so its holdsExtra values are kept
      // locally here but not actually persisted until it's edited again
      // after the whole booking (and this hold's real holdID) is saved —
      // same disclosed limitation as this app's other list-child extra
      // tables.
      const {
        holdsExtraFieldKeys = [],
        holdsExtraTypeMap = {},
        holdsExtraID = 0,
      } = holdFormRef?.current || {};
      const holdsExtraData = {};
      holdsExtraFieldKeys.forEach((k) => {
        const val = value?.[k];
        const htmlType = holdsExtraTypeMap[k];
        holdsExtraData[k] = val
          ? htmlType === "date"
            ? dayjs(val).format("MM/DD/YYYY")
            : htmlType === "datetime"
              ? dayjs(val).format("MM/DD/YYYY HH:mm")
              : val
          : null;
        delete value[k];
      });

      let holds = holdsData; // This seems unused, consider removing if unnecessary
      if (isEdit) {
        holds = holdsData?.map((item) => {
          if (item?.uuid === selectedHold?.uuid) {
            const existingExtra = item?.holdsExtra?.[0] || {};
            return {
              ...item,
              ...value,
              holdsExtra:
                holdsExtraFieldKeys.length > 0
                  ? [
                      {
                        ...existingExtra,
                        ...holdsExtraData,
                        holdsExtraID:
                          existingExtra?.holdsExtraID ?? holdsExtraID ?? 0,
                        holdID: existingExtra?.holdID ?? item?.holdID ?? 0,
                      },
                    ]
                  : item?.holdsExtra,
            };
          } else {
            return item;
          }
        });
      } else {
        holds?.push({
          ...value,
          holdsExtra:
            holdsExtraFieldKeys.length > 0
              ? [{ ...holdsExtraData, holdsExtraID: 0, holdID: 0 }]
              : undefined,
        });
      }

      dispatch(
        setBookings({
          holds: holds,
        })
      );

      cancelAction();
    });
  };

  const cancelAction = () => {
    setSelectedHold(null);
    setHoldsModal(false);
    setisEdit(false);
  };

  const deleteHold = () => {
    const holds = holdsData?.filter((h) => h?.uuid !== selectedHold?.uuid);
    dispatch(
      setBookings({
        holds: holds,
      })
    );
    const url = API_ENDPOINTS.hold + "/id/" + selectedHold?.holdID;
    dispatch(setDeleteAPis(url));
    cancelAction();
  };

  return (
    <>
      <StyledGridRow justify="end">
        <StyledGridCol style={{ paddingBottom: "10px" }}>
          <StyledFlex align="center" gap="10px">
            <Button
              disabled={
                !bookingsFieldsEnabled ||
                isConfiguring ||
                !hasCheckSecurity(
                  Security_Types.functionality,
                  Entity_types.subModule,
                  LABELS_CAPITAL.ADD,
                  MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                  SUB_MODULES_LIST_WITH_ID.Arrest_Report
                )
              }
              onClick={onAddHolds}
            >
              <span>
                <PlusOutlined />
              </span>{" "}
              Add
            </Button>
            <Button
              onClick={editHold}
              disabled={
                selectedHold === null ||
                isConfiguring ||
                !hasCheckSecurity(
                  Security_Types.functionality,
                  Entity_types.subModule,
                  LABELS_CAPITAL.VIEW,
                  MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                  SUB_MODULES_LIST_WITH_ID.Arrest_Report
                )
              }
            >
              Details
            </Button>
            <Popconfirm
              title="Delete the Hold"
              description="Are you sure to delete this Hold?"
              onConfirm={deleteHold}
              onCancel={() => {}}
              okText="Yes"
              cancelText="No"
            >
              <Button
                color="primary"
                type="primary"
                disabled={
                  !bookingsFieldsEnabled ||
                  selectedHold === null ||
                  isConfiguring ||
                  !hasCheckSecurity(
                    Security_Types.functionality,
                    Entity_types.subModule,
                    LABELS_CAPITAL.DELETE,
                    MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
                    SUB_MODULES_LIST_WITH_ID.Arrest_Report
                  )
                }
              >
                Delete
              </Button>
            </Popconfirm>
            <Button
              onClick={cancelAction}
              color="primary"
              type="primary"
              disabled={selectedHold === null || isConfiguring}
            >
              Cancel
            </Button>

            {/* Config — drag-reorder/resize columns; only while Modify is active */}
            {!isConfiguring ? (
              <Button
                htmlType="button"
                onClick={startConfiguring}
              >
                {configButtonLabel}
              </Button>
            ) : (
              <Popconfirm
                title="Do you want to save the current list view configuration?"
                onConfirm={confirmSave}
                onCancel={discardChanges}
                okText="Yes"
                cancelText="No"
              >
                <Button htmlType="button">
                  {configButtonLabel}
                </Button>
              </Popconfirm>
            )}
          </StyledFlex>
        </StyledGridCol>
      </StyledGridRow>

      {hasCheckSecurity(
        Security_Types.functionality,
        Entity_types.subModule,
        LABELS_CAPITAL.VIEW,
        MODULES_LIST_WITH_ID.MODULE_BOOKINGS,
        SUB_MODULES_LIST_WITH_ID.Arrest_Report
      ) && (
        <Table
          ref={dataTableRef}
          headers={holdsHeaders}
          draggable={isConfiguring}
          data={holdsTableData}
          onRowClick={patchSelectedHoldData}
          onRowDoubleClick={(data) => {
            patchSelectedHoldData(data);
            editHold();
          }}
          selected={selectedHold === null}
        />
      )}

      {holdsModal && (
        <BookingHoldsModal
          show={holdsModal}
          holdFormRef={holdFormRef}
          selectedHold={selectedHold}
          updateStoreObj={updateStoreObj}
          onHide={cancelAction}
          isEdit={isEdit}
          setisEdit={setisEdit}
          startingPoint={startingPoint}
        />
      )}
    </>
  );
};
export default BookingHolds;
